import React, { useState } from "react";
import {
  Activity,
  Calendar,
  DollarSign,
  Heart,
  MessageSquare,
  PieChart,
  Settings,
  Target,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
  X,
  AlertTriangle
} from "lucide-react";
import {useIsAdmin} from '../hooks/useIsAdmin.ts';
import {useGetProfile} from '../hooks/useProfile.ts';
import {useDashboardSummary, useKitTransactions, useMemberDashboardData} from '../hooks/useDashboard.ts';
import { useGetUnpaidFines } from '../hooks/useFines.ts';
import {useGroupFeatures} from '../hooks/useGroupFeatures.ts';
import {Link} from 'react-router-dom';
import MemberContributionFrequencyChart from '../components/MemberContributionFrequencyChart.tsx';
import TopContributorsChart from '../components/TopContributorsChart';
import UpgradePromptCard from '../components/UpgradePromptCard';
import SmsQuotaWidget from '../components/SmsQuotaWidget';
import HouseManagementDashboard from './HouseManagementDashboard';

const PRICING_ANNOUNCEMENT_ID = 'advanced-plan-price-update-2026-09-v2';

export default function Dashboard() {
  const isAdmin = useIsAdmin();
  const { data: profile } = useGetProfile();
  const [isPricingBannerDismissed, setIsPricingBannerDismissed] = useState(() => {
    return localStorage.getItem(`dismissed_${PRICING_ANNOUNCEMENT_ID}`) === 'true';
  });

  const handleDismissPricingBanner = () => {
    localStorage.setItem(`dismissed_${PRICING_ANNOUNCEMENT_ID}`, 'true');
    setIsPricingBannerDismissed(true);
  };


  // Get group features to filter dashboard sections
  const { selectedFeatures: groupSelectedFeatures, isLoading: isLoadingFeatures } = useGroupFeatures();
  
  // Fetch dashboard data
  const { data: dashboardData, isLoading: isDashboardLoading, error: dashboardError } = useDashboardSummary();
  
  // Fetch recent transactions for Recent Activity
  const { data: transactionsData } = useKitTransactions({ 
    page: 1, 
    per_page: 10 
  });

  // Fetch member dashboard data
  const { data: memberDashboardData, error: memberDashboardError, isLoading: memberDashboardLoading } = useMemberDashboardData();

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = (featureName) => {
    if (isLoadingFeatures || !groupSelectedFeatures || groupSelectedFeatures.length === 0) {
      return true; // Show all features while loading or if no features selected
    }
    return groupSelectedFeatures.includes(featureName);
  };
  
  const { data: unpaidMemberFines} = useGetUnpaidFines({
    page: 1, 
    per_page: 3
  }
  );
  // Extract data from dashboard summary
  const dashboard = dashboardData || {};
  
  // Calculate metrics from real data
  const totalMembers = dashboard.members?.total || 0;
  const activeMembers = dashboard.members?.active || 0;
  const totalContributions = dashboard.contributions?.total || 0;
  const thisMonthContributions = dashboard.contributions?.this_month || 0;
  const todayContributions = dashboard.contributions?.today || 0;
  const activeLoans = dashboard.loans?.active || 0;
  const overdueLoans = dashboard.loans?.overdue || 0;
  const groupBalance = dashboard.group_balance || 0;
  const upcomingMeeting = dashboard.upcoming_meeting;
  const bulkSms = dashboard.bulk_sms;
  const shares = dashboard.shares;
  
  // Use transactions data for recent activity
  const recentTransactions = transactionsData?.data || [];
  
  // Calculate trends (using contribution trend data)
  const contributionTrend = dashboard.contributions?.trend || [];

  // Generate alerts based on real data
  const alerts = [];
  
  if (overdueLoans > 0) {
    alerts.push({
      type: 'overdue',
      message: `${overdueLoans} loan${overdueLoans > 1 ? 's are' : ' is'} overdue`,
      count: overdueLoans,
      priority: 'high'
    });
  }
  
  if (upcomingMeeting && upcomingMeeting.date) {
    const meetingDate = new Date(upcomingMeeting.date);
    const now = new Date();
    const daysUntilMeeting = Math.ceil((meetingDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilMeeting <= 2) {
      alerts.push({
        type: 'meeting',
        message: `Meeting in ${daysUntilMeeting} day${daysUntilMeeting > 1 ? 's' : ''}`,
        count: 1,
        priority: daysUntilMeeting === 0 ? 'high' : 'medium'
      });
    }
  }
  
  if (parseFloat(groupBalance) < 1000) {
    alerts.push({
      type: 'balance',
      message: 'Group balance is below recommended level',
      count: 1,
      priority: 'medium'
    });
  }

  // Check for pending shares
  if (shares && shares.pending > 0) {
    alerts.push({
      type: 'shares',
      message: `${shares.pending} share request${shares.pending > 1 ? 's' : ''} pending approval`,
      count: shares.pending,
      priority: 'medium'
    });
  }

  // Check SMS credits
  if (bulkSms && bulkSms.credits_remaining < 10) {
    alerts.push({
      type: 'sms',
      message: `Low SMS credits remaining: ${bulkSms.credits_remaining}`,
      count: 1,
      priority: 'medium'
    });
  }
  
  // If no alerts, show a positive message
  if (alerts.length === 0) {
    alerts.push({
      type: 'success',
      message: 'All systems running smoothly',
      count: 1,
      priority: 'low'
    });
  }

  const formatTransactionMessage = (transaction) => {
    const memberName = transaction.member?.name;
    const amount = Number(transaction.amount).toLocaleString();
    
    switch (transaction.type) {
      case 'contribution':
        return memberName ? `Contribution of KSh ${amount} by ${memberName}` : `Contribution of KSh ${amount}`;
      case 'loan':
        return memberName ? `Loan of KSh ${amount} to ${memberName}` : `Loan of KSh ${amount}`;
      case 'repayment':
        return memberName ? `Repayment of KSh ${amount} by ${memberName}` : `Repayment of KSh ${amount}`;
      case 'withdrawal':
        return memberName ? `Withdrawal of KSh ${amount} by ${memberName}` : `Withdrawal of KSh ${amount}`;
      case 'deposit':
        return memberName ? `Deposit of KSh ${amount} by ${memberName}` : `Deposit of KSh ${amount}`;
      case 'transfer':
        return memberName ? `Transfer of KSh ${amount} by ${memberName}` : `Transfer of KSh ${amount}`;
      default:
        return memberName ? `${transaction.type} transaction of KSh ${amount} by ${memberName}` : `${transaction.type} transaction of KSh ${amount}`;
    }
  };

  const getTransactionStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-600';
      case 'pending': return 'bg-yellow-100 text-yellow-600';
      case 'failed': return 'bg-red-100 text-red-600';
      default: return 'bg-green-100 text-green-600';
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    
    return dateString;
  };

  if (Number(profile?.data?.current_group?.is_house_management) === 1) {
    return <HouseManagementDashboard />;
  }

  if (isDashboardLoading) {
    return (
      <div className="min-h-screen py-4 px-1 md:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (dashboardError) {
    return (
      <div className="min-h-screen py-4 px-1 md:px-0">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-red-600 text-xl font-semibold mb-4">Error Loading Dashboard</div>
          <div className="text-gray-600 mb-4">There was an issue loading the dashboard data.</div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-1 md:px-0">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 rounded-full">v2.4</span>
            </div>
            <p className="text-gray-600 mt-1">
              {isAdmin 
                ? "Welcome back! Here's what's happening with your group." 
                : `Welcome back, ${memberDashboardData?.personal_info?.name || profile?.name || 'Member'}! Here's your personal overview.`
              }
            </p>
          </div>
          </div>

        {/* Advanced Plan Pricing Update Banner (Admin Only) */}
        {isAdmin && !isPricingBannerDismissed && (
          <div className="relative bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-4">
            <button
              onClick={handleDismissPricingBanner}
              className="absolute top-4 right-4 text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200 transition-colors"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
            <div className="flex items-start gap-3 pr-8">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-amber-900 dark:text-amber-100 text-sm mb-1">
                  Advanced Plan pricing update
                </h3>
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  The Advanced Plan price has increased from KSh 500 to KSh 999 due to rising server management fees.
                  This takes effect in your next billing cycle. We've also retired the free plan — new groups now get
                  a 7-day trial period instead.{' '}
                  <a
                    href="https://twachanga.co.ke/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline hover:text-amber-900 dark:hover:text-amber-100"
                  >
                    View pricing details →
                  </a>
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-2">
                  We've also released a new version of our mobile app.{' '}
                  <a
                    href="https://play.google.com/store/apps/details?id=co.ke.twachanga"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline hover:text-amber-900 dark:hover:text-amber-100"
                  >
                    Get it on Google Play →
                  </a>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Prompt for Free Plan Users (Admin Only) */}
        {isAdmin && <UpgradePromptCard />}

        {/* Key Metrics Cards - Different for Admin vs Member */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 kpi-grid">
          {isAdmin ? (
            // Admin View - Group Overview
            <>

              {/* Total Contributions - Always show if manual_contribution_management is enabled */}
              {isFeatureEnabled('manual_contribution_management') && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Contributions</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">
                        KSh {Number(totalContributions).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>
              )}

              {/* This Month Contributions - Show if manual_contribution_management is enabled */}
              {isFeatureEnabled('manual_contribution_management') && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Today</p>
                        <p className="text-2xl font-bold text-gray-900 mt-2">
                          KSh {Number(todayContributions).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Contributions</p>
                      </div>
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                    </div>
                  </div>
              )}

              {/* This Month Contributions - Show if manual_contribution_management is enabled */}
              {isFeatureEnabled('manual_contribution_management') && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">This Month</p>
                        <p className="text-2xl font-bold text-gray-900 mt-2">
                          KSh {Number(thisMonthContributions).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Contributions</p>
                      </div>
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                    </div>
                  </div>
              )}

              {/* Total Members - Always show if members_management is enabled */}
              {isFeatureEnabled('members_management') && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Members</p>
                        <p className="text-3xl font-bold text-gray-900 mt-2">{totalMembers}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>
              )}

            </>
          ) : (
            // Member View - Personal Overview
            <>
              {/* My Contributions - Show if manual_contribution_management is enabled */}
              {isFeatureEnabled('manual_contribution_management') && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">My Contributions</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">
                        KSh {Number(memberDashboardData?.contributions?.total || profile?.total_contributions || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">This month: KSh {Number(memberDashboardData?.contributions?.this_month || profile?.monthly_contributions || 0).toLocaleString()}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>
              )}

              {/* My Loans - Show if basic_loan_management or advanced_loan_management is enabled */}
              {(isFeatureEnabled('basic_loan_management') || isFeatureEnabled('advanced_loan_management')) && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">My Loans</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{memberDashboardData?.loans?.active || profile?.active_loans || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">Active loans</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>
              )}

              {/* My Shares - Show if shares_management is enabled */}
              {isFeatureEnabled('shares_management') && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">My Shares</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{memberDashboardData?.shares?.total_shares || profile?.total_shares || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">KSh {Number(memberDashboardData?.shares?.total_value || profile?.shares_value || 0).toLocaleString()} value</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <PieChart className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>
              )}

              {/* Next Meeting - Show if meetings is enabled */}
              {isFeatureEnabled('meetings') && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Next Meeting</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">
                        {memberDashboardData?.upcoming_meeting?.date ? new Date(memberDashboardData.upcoming_meeting.date).getDate() : (upcomingMeeting?.date ? new Date(upcomingMeeting.date).getDate() : 'TBD')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {memberDashboardData?.upcoming_meeting?.date ? new Date(memberDashboardData.upcoming_meeting.date).toLocaleDateString() : (upcomingMeeting?.date ? new Date(upcomingMeeting.date).toLocaleDateString() : 'No upcoming meetings')}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>


        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {isAdmin ? (
            // Admin View
            <>
              {/* Recent Transactions */}
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
                  <Link to="/transactions" className="text-sm text-blue-800 hover:text-blue-700 font-medium">
                    View All →
                  </Link>
                </div>
                <div className="space-y-4">
                  {recentTransactions.length > 0 ? (
                    recentTransactions.map((transaction, index) => {
                      // Determine if transaction is addition (credit) or subtraction (debit)
                      // Using exact same logic as TransactionsPage.js
                      let isAddition = false;
                      if (transaction.type === 'mpesa') {
                        isAddition = true;
                      } else if (transaction.type === 'group_kit') {
                        isAddition = transaction.raw_payload?.operation === 'add';
                      }
                      // All other transaction types default to debit (red)
                      
                      return (
                        <div key={index} className="flex items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900">
                                {formatTransactionMessage(transaction)}
                              </p>
                              <span className={`font-bold ${isAddition ? 'text-green-600' : 'text-red-600'}`}>
                                {isAddition ? '+' : '-'}KSh {Number(transaction.amount).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {(() => {
                                const dateField = transaction.transaction_date ?? transaction.created_at;
                                const timeAgo = formatTimeAgo(dateField);
                                
                                // If we get "Invalid date" or "Future date", show the raw date
                                if (timeAgo === 'Invalid date' || timeAgo === 'Future date') {
                                  return dateField ? new Date(dateField).toLocaleDateString() : 'Unknown date';
                                }

                                return timeAgo;
                              })()}
                            </p>
                            {transaction.member && (
                              <p className="text-xs text-gray-400">Member: {transaction.member.name}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTransactionStatusColor(transaction.status)}`}>
                              {transaction.status}
                            </span>
                            <span className="text-xs text-gray-500 mt-1 capitalize">
                              {transaction.type.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No recent transactions</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Insights */}
              <div className="rounded-2xl shadow-sm border p-6 bg-white">
                <h3 className="text-lg font-semibold mb-4">
                  Quick Insights
                </h3>

                <div className="space-y-4">
                  {/* SMS Quota Widget - Show if bulk_sms is enabled */}
                  {isFeatureEnabled('bulk_sms') && isAdmin && (
                    <SmsQuotaWidget onRechargeClick={() => window.location.href = '/settings?tab=sms-quota'} />
                  )}

                  {/* Group Health Score */}
                  <div className="p-4 rounded-lg border bg-blue-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        Group Health
                      </span>
                      <span className="text-lg font-bold text-blue-600">
                        {Math.round((activeMembers / totalMembers) * 100)}%
                      </span>
                    </div>

                    <div className="w-full rounded-full h-2 bg-blue-200">
                      <div
                        className="h-2 rounded-full transition-all duration-300 bg-blue-600"
                        style={{ width: `${(activeMembers / totalMembers) * 100}%` }}
                      />
                    </div>

                    <p className="text-xs mt-1">
                      {activeMembers} of {totalMembers} members active
                    </p>
                  </div>

                  {/* Quick Actions */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">
                      Quick Actions
                    </h4>

                    <div className="grid grid-cols-2 gap-2">
                      {isFeatureEnabled('manual_contribution_management') && (
                        <Link
                          to="/contributions"
                          className="p-2 rounded-lg text-center transition-colors
                                    bg-blue-50 hover:bg-blue-100"
                        >
                          <DollarSign className="w-4 h-4 mx-auto mb-1 text-blue-600" />
                          <span className="text-xs">Record Payment</span>
                        </Link>
                      )}

                      {isFeatureEnabled('members_management') && (
                        <Link
                          to="/members"
                          className="p-2 rounded-lg text-center transition-colors
                                    bg-green-50 hover:bg-green-100"
                        >
                          <UserPlus className="w-4 h-4 mx-auto mb-1 text-green-800" />
                          <span className="text-xs">Add Member</span>
                        </Link>
                      )}

                      {isFeatureEnabled('meetings') && (
                        <Link
                          to="/meetings"
                          className="p-2 rounded-lg text-center transition-colors
                                    bg-purple-50 hover:bg-purple-100"
                        >
                          <Calendar className="w-4 h-4 mx-auto mb-1 text-purple-800" />
                          <span className="text-xs">Schedule Meeting</span>
                        </Link>
                      )}

                      {isFeatureEnabled('bulk_sms') && (
                        <Link
                          to="/bulk-sms"
                          className="p-2 rounded-lg text-center transition-colors
                                    bg-purple-50 hover:bg-purple-100"
                        >
                          <MessageSquare className="w-4 h-4 mx-auto mb-1 text-orange-800" />
                          <span className="text-xs">Send SMS</span>
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Fun Fact */}
                  {isFeatureEnabled('manual_contribution_management') && (
                    <div className="p-3 rounded-lg border bg-yellow-50">
                      <div className="flex items-center">
                        <Target className="w-4 h-4 mr-2 text-yellow-800" />
                        <span className="text-xs font-medium">
                          Did you know?
                        </span>
                      </div>

                      <p className="text-xs mt-1">
                        Your group has collected KSh{" "}
                        {Number(totalContributions).toLocaleString()} in total contributions!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            // Member View
            <>
              {/* My Recent Activity - Show if manual_contribution_management is enabled */}
              {isFeatureEnabled('manual_contribution_management') && (
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Your Recent Contributions</h3>
                    <Link to="/contributions" className="text-sm text-blue-800 hover:text-blue-700 font-medium">
                      View All →
                    </Link>
                  </div>
                  <div className="space-y-4">
                    {memberDashboardData?.recent_contributions?.length > 0 ? (
                      memberDashboardData.recent_contributions.map((contribution, index) => (
                        <div key={index} className="flex items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 bg-green-100">
                            <DollarSign className="w-4 h-4 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900">
                                KSh {Number(contribution.amount).toLocaleString()}
                              </p>
                              {contribution.welfare_plan_name && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 text-xs font-semibold">
                                  <Heart size={12} className="mr-1 text-pink-500" />
                                  {contribution.welfare_plan_name}
                                </span>
                              )}
                              {contribution.drive_name && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                                  {contribution.drive_name}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">{contribution.contribution_date}</p>
                            {contribution.notes && (
                              <p className="text-xs text-gray-400 mt-1">{contribution.notes}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-600">
                              Completed
                            </span>
                            <span className="text-xs text-gray-500 mt-1">
                              Contribution
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <DollarSign className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No recent contributions</p>
                        <p className="text-xs text-gray-400 mt-1">Start contributing to see your history here</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Personal Insights */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Personal Insights</h3>
                <div className="space-y-4">
                  {/* Quick Actions for Members */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-200">Quick Actions</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {isFeatureEnabled('manual_contribution_management') && (
                        <Link 
                          to="/contributions" 
                          className="p-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-lg text-center transition-colors border border-transparent hover:border-blue-200 dark:border-blue-800/50 dark:hover:border-blue-700"
                        >
                          <DollarSign className="w-4 h-4 text-blue-600 dark:text-blue-400 mx-auto mb-1" />
                          <span className="text-xs text-blue-700 dark:text-blue-300">Make Payment</span>
                        </Link>
                      )}
                      {(isFeatureEnabled('basic_loan_management') || isFeatureEnabled('advanced_loan_management')) && (
                        <Link 
                          to="/member-loans" 
                          className="p-2 bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 rounded-lg text-center transition-colors border border-transparent hover:border-purple-200 dark:border-purple-800/50 dark:hover:border-purple-700"
                        >
                          <TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400 mx-auto mb-1" />
                          <span className="text-xs text-purple-700 dark:text-purple-300">Apply Loan</span>
                        </Link>
                      )}
                      {isFeatureEnabled('shares_management') && (
                        <Link 
                          to="/shares" 
                          className="p-2 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 rounded-lg text-center transition-colors border border-transparent hover:border-orange-200 dark:border-orange-800/50 dark:hover:border-orange-700"
                        >
                          <PieChart className="w-4 h-4 text-orange-600 dark:text-orange-400 mx-auto mb-1" />
                          <span className="text-xs text-orange-700 dark:text-orange-300">View Shares</span>
                        </Link>
                      )}
                      <Link 
                        to="/settings" 
                        className="p-2 bg-green-50 hover:bg-green-100 dark:bg-green-900/30 dark:hover:bg-green-900/50 rounded-lg text-center transition-colors border border-transparent hover:border-green-200 dark:border-green-800/50 dark:hover:border-green-700"
                      >
                        <Settings className="w-4 h-4 text-green-600 dark:text-green-400 mx-auto mb-1" />
                        <span className="text-xs text-green-700 dark:text-green-300">Settings</span>
                      </Link>
                    </div>
                  </div>

                  {/* Personal Achievement */}
                  {isFeatureEnabled('manual_contribution_management') && (
                    <div className="p-3 bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/30 dark:to-amber-900/30 rounded-lg border border-yellow-100 dark:border-yellow-800/50">
                      <div className="flex items-center">
                        <Target className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                        <span className="text-xs text-yellow-800 dark:text-yellow-200 font-medium">Your Achievement</span>
                      </div>
                      <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                        You've contributed KSh {Number(profile?.total_contributions || 0).toLocaleString()} to the group!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
            
        {/* Additional Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 kpi-grid">
          {isAdmin ? (
            // Admin View - Group Statistics
            <>
              {/* Group Balance - Always show for admin */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Group Balance</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      KSh {Number(groupBalance).toLocaleString()}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </div>

              {/* Active Loans - Show if basic_loan_management or advanced_loan_management is enabled */}
              {(isFeatureEnabled('basic_loan_management') || isFeatureEnabled('advanced_loan_management')) && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Active Loans</p>
                        <p className="text-3xl font-bold text-gray-900 mt-2">{activeLoans}</p>
                      </div>
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                  </div>
              )}

              {/* Shares - Show if shares_management is enabled */}
              {isFeatureEnabled('shares_management') && shares && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Shares</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{shares.total_shares}</p>
                      <p className="text-xs text-gray-500 mt-1">KSh {Number(shares.total_value).toLocaleString()} total</p>
                    </div>
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <PieChart className="w-5 h-5 text-orange-600" />
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Member View - Personal Statistics
            <>
              {/* My Monthly Progress - Show if manual_contribution_management is enabled */}
              {isFeatureEnabled('manual_contribution_management') && (
                <div className="rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">This Month</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">
                        KSh {Number(memberDashboardData?.contributions?.this_month || profile?.monthly_contributions || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">My contributions</p>
                    </div>
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                </div>
              )}

              {/* My Loan Balance - Show if basic_loan_management or advanced_loan_management is enabled */}
              {(isFeatureEnabled('basic_loan_management') || isFeatureEnabled('advanced_loan_management')) && (
                <div className="rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Loan Balance</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">
                        KSh {Number(memberDashboardData?.loans?.total_balance || profile?.loan_balance || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{memberDashboardData?.loans?.active || profile?.active_loans || 0} active loans</p>
                    </div>
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Days Since Last Payment - Show if manual_contribution_management is enabled */}
              {isFeatureEnabled('manual_contribution_management') && (
                <div className="rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Last Payment</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">
                        {memberDashboardData?.contributions?.days_since_last_payment || profile?.days_since_last_payment || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">days ago</p>
                    </div>
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-orange-600" />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Upcoming Events - Different for Admin vs Member */}
        <div className="rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {isAdmin ? 'Upcoming Events' : 'My Upcoming Events'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Next Meeting - Show if meetings is enabled */}
            {isFeatureEnabled('meetings') && upcomingMeeting && upcomingMeeting.date ? (
              <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-gray-900">Next Meeting</span>
                </div>
                <p className="text-sm text-gray-600">
                  {new Date(upcomingMeeting.date).toLocaleDateString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">{upcomingMeeting.title || 'TBD'}</p>
              </div>
            ) : isFeatureEnabled('meetings') ? (
              <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-500">No upcoming meetings</span>
                </div>
              </div>
            ) : null}
            
            {isAdmin ? (
              // Admin-only events
              <>
                {/* Pending Shares - Show if shares_management is enabled */}
                {isFeatureEnabled('shares_management') && shares && shares.pending > 0 && (
                  <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <PieChart className="w-5 h-5 text-orange-600 mr-2" />
                      <span className="text-sm font-medium text-gray-900">Pending Shares</span>
                    </div>
                    <p className="text-sm text-gray-600">{shares.pending} request{shares.pending > 1 ? 's' : ''}</p>
                    <p className="text-xs text-gray-500 mt-1">Awaiting approval</p>
                  </div>
                )}

                {/* Low SMS Credits - Show if bulk_sms is enabled */}
                {isFeatureEnabled('bulk_sms') && bulkSms && bulkSms.credits_remaining < 10 && (
                  <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <MessageSquare className="w-5 h-5 text-red-600 mr-2" />
                      <span className="text-sm font-medium text-gray-900">Low SMS Credits</span>
                    </div>
                    <p className="text-sm text-gray-600">{bulkSms.credits_remaining} remaining</p>
                    <p className="text-xs text-gray-500 mt-1">Consider recharging</p>
                  </div>
                )}
              </>
            ) : (
              // Member-specific events
              <>
                {/* Next Loan Payment - Show if basic_loan_management or advanced_loan_management is enabled */}
                {(isFeatureEnabled('basic_loan_management') || isFeatureEnabled('advanced_loan_management')) && profile?.next_loan_due && (
                  <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <TrendingUp className="w-5 h-5 text-purple-600 mr-2" />
                      <span className="text-sm font-medium text-gray-900">Next Loan Payment</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {new Date(profile.next_loan_due).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">KSh {Number(profile?.next_loan_amount || 0).toLocaleString()}</p>
                  </div>
                )}

                {/* Welfare Due - Show if welfare_management is enabled */}
                {isFeatureEnabled('welfare_management') && profile?.welfare_contribution_due && (
                  <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <Heart className="w-5 h-5 text-pink-600 mr-2" />
                      <span className="text-sm font-medium text-gray-900">Welfare Due</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {new Date(profile.welfare_contribution_due).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">KSh {Number(profile?.welfare_amount || 0).toLocaleString()}</p>
                  </div>
                )}

                {/* Next Loan Payment from member dashboard data - Show if basic_loan_management or advanced_loan_management is enabled */}
                {(isFeatureEnabled('basic_loan_management') || isFeatureEnabled('advanced_loan_management')) && memberDashboardData?.loans?.next_payment_due && (
                  <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <TrendingUp className="w-5 h-5 text-purple-600 mr-2" />
                      <span className="text-sm font-medium text-gray-900">Next Loan Payment</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {new Date(memberDashboardData.loans.next_payment_due).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">KSh {Number(memberDashboardData.loans.next_payment_amount || 0).toLocaleString()}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>


           {/* Unpaid fines - Different for Admin vs Member */}
           {
        
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {/* Fines Table */}
          <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">
              Unpaid Fines
            </h4>
            {isAdmin && (
            <Link to="/fines" className="text-sm text-blue-800 hover:text-blue-500 font-medium">
                    View All →
            </Link>
            )}

            <div className="overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200 dark:divide-gray-700 border rounded-xl">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Amount (Ksh)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Fine balance (Ksh)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Member
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Reason
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Issued At
                    </th>
                  </tr>
                </thead>


              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {unpaidMemberFines?.length > 0 ? (
                  unpaidMemberFines.map((fine) => (
                    <tr key={fine.id} className="hover:bg-gray-50 transition">
                      <td data-label="Amount (Ksh)" className="px-4 py-3 text-sm text-gray-900">
                        {fine.amount}
                      </td>

                      <td data-label="Fine balance (Ksh)" className="px-4 py-3 text-sm text-gray-900">
                        {fine.fine_balance}
                      </td>
                      <td data-label="Member" className="px-4 py-3">
                        <span
                          className={`
                            px-2 py-1 rounded-full text-xs font-medium
                            
                          `}
                        >
                          {fine.member.name}
                        </span>
                      </td>

                      <td data-label="Reason" className="px-4 py-3 text-sm text-gray-600">
                        {fine.reason}
                      </td>

                      <td data-label="Issued At" className="px-4 py-3 text-sm text-gray-600">
                        {new Date(fine.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-4 py-6 text-center text-sm text-gray-500"
                    >
                      No fines to display
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
       </div>

        </div>

}

        {/* Contribution Trends Chart - Only for Admin */}
        {isAdmin && isFeatureEnabled('manual_contribution_management') && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contribution Trends</h3>
            <div className="h-64 flex items-end justify-between space-x-2 relative">
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-500 pr-2">
                {(() => {
                  const maxAmount = Math.max(...contributionTrend.map(d => parseFloat(d.amount || 0)));
                  const steps = 5;
                  const stepValue = maxAmount / steps;
                  return Array.from({ length: steps + 1 }, (_, i) => {
                    const value = Math.round(stepValue * i);
                    return (
                      <div key={i} className="flex items-center">
                        <span>KSh {Number(value).toLocaleString()}</span>
                      </div>
                    );
                  }).reverse();
                })()}
              </div>
              
              {/* Chart bars with proper spacing */}
              <div className="flex-1 flex items-end justify-between space-x-2 ml-12">
                {contributionTrend.map((data, index) => {
                  const maxAmount = Math.max(...contributionTrend.map(d => parseFloat(d.amount || 0)));
                  const height = maxAmount > 0 ? (parseFloat(data.amount || 0) / maxAmount) * 200 : 0;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-blue-500 rounded-t transition-all duration-300 hover:bg-blue-600 relative group"
                        style={{ height: `${height}px` }}
                        title={`${data.month}: KSh ${Number(data.amount || 0).toLocaleString()}`}
                      >
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                          KSh {Number(data.amount || 0).toLocaleString()}
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 mt-2 text-center">{data.month}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">Monthly contribution trends (last 6 months)</p>
            </div>
          </div>
        )}
        
        {/* Top Contributors Chart - Shows top 10 contributors with rankings (Admin only) */}
        {isAdmin && isFeatureEnabled('manual_contribution_management') && dashboard && (
          <TopContributorsChart accountNoTransactionStats={dashboard.accountNoTransactionStats} />
        )}
      </div>
    </div>
  );
}


